import { getMpcWalletAddressesQuery } from '@polaris/core/keys'
import {
  type RouteMachineInput,
  routeMachine,
  setGasStationMachineLogger,
  setRouteMachineLogger,
  shouldSkipGasStation,
  shouldTransferFundsToAutopilotWallet,
} from '@polaris/core/machines'
import { ChainType, type RouteModel } from '@polaris/core/models'
import { LIT_ACTIONS_VERSION } from '@polaris/core/mpc'
import type { Step } from '@polaris/core/services'
import {
  getChainTypeToEcosystemAddressesKey,
  getRequiredAddressIfDifferent,
} from '@polaris/core/services'
import { getEcosystemAddresses } from '@polaris/core/signing'
import { featureFlagsAtom } from '@polaris/core/stores'
import { mpcKeyHierarchyAtom } from '@polaris/core/stores/mpc/atom'
import {
  formatPrice,
  getDeepError,
  humanizeSeconds,
  UserRejectedRequestError,
} from '@polaris/core/utils'
import { atom, type Getter } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithActor, atomWithActorSnapshot } from 'jotai-xstate'
import { ampli } from '@/src/codegen/ampli'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { invalidateHistory } from '@/src/queries/keys/history'
import { webChainDataProvider } from '@/src/services/chain-data-provider'
import { safeTrackSwapError } from '@/src/services/error-tracker'
import { log } from '@/src/services/logger'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { walletKeyringAtom } from '@/src/stores/wallets/atoms'
import { getCurrentPageType } from '@/src/utils/url'
import {
  makeSwapSignedComplete,
  makeSwapSignedProperties,
} from '../../machines/ampli-event-mapper'
import { logEventFromRouteMachine } from '../../machines/route-machine-emitted'
import { balancesByChainsAtom } from '../balances/atom'
import {
  getChainAccountAtom,
  getConvertedChainAddressAtom,
} from '../chains/atoms'
import { cosmosDerivationPathsAtom } from '../hotkeys/atoms'
import { isCurrentStepModeAtom } from '../trade/atoms'
import { walletOptionsAtom } from '../wallets/atoms'
import { onErrorInvalidate, onErrorToasts } from './events'

//#region Machine Init

const getInitialInput = ({ get }: { get: Getter }): RouteMachineInput => {
  const walletKeyring = get(walletKeyringAtom)
  const { privacy, derivedPrivacySigners } = get(featureFlagsAtom)
  const route = get(routeAtom)
  const cosmosDerivationPaths = get(cosmosDerivationPathsAtom)
  const keyHierarchy = get(mpcKeyHierarchyAtom)

  const isKeyHierarchyValid =
    keyHierarchy?.rootKeyId !== undefined &&
    keyHierarchy?.runnerSecretId !== undefined &&
    keyHierarchy?.version === LIT_ACTIONS_VERSION

  return {
    walletKeyring,
    route: route!,
    enablePrivacy: privacy,
    derivedPrivacySigners,
    cosmosDerivationPaths,
    chainDataProvider: webChainDataProvider,
    getMpcWalletAddresses: async (sessionKeyData) => {
      return getQueryClient().ensureQueryData(
        getMpcWalletAddressesQuery(
          {
            sessionKeyData,
          },
          {
            enabled: isKeyHierarchyValid,
          },
        ),
      )
    },
  }
}

export const routeAtom = atom<RouteModel>()

export const routeMachineAtom = atomWithActor(
  () => routeMachine.provide({}),
  (get) => ({
    input: getInitialInput({ get }),
  }),
)
//#endregion

//#region Actors snapshots
export const routeMachineSnapshotAtom = atomWithActorSnapshot((get) => {
  const routeMachine = get(routeMachineAtom)
  const isCurrentStepMode = get(isCurrentStepModeAtom)
  const balances = get(balancesByChainsAtom)

  onErrorToasts(routeMachine, isCurrentStepMode)
  onErrorInvalidate(routeMachine, balances)

  return routeMachine
})

//#endregion

//#region Route machine context derivated states and global states

const isFirstStepAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.context.currentStep === 1
})

const isLastStepAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return (
    routeMachineSnapshot.context.currentStep ===
    (routeMachineSnapshot.context.route?.Steps.length ?? 0) - 1
  )
})

export const errorAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)
  const stepError = get(currentStepErrorAtom)
  const hotkeyTransferError = get(hotkeyTransferErrorAtom)

  return snapshot.context.error ?? stepError ?? hotkeyTransferError
})

export const isUserRejectionErrorAtom = atom((get) => {
  const error = get(errorAtom)

  return error ? getDeepError(error) instanceof UserRejectedRequestError : false
})

const isFirstStepLoadingAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  const isStepLoading = get(isCurrentStepLoadingAtom)

  return routeMachineSnapshot.context.currentStep === 1 && isStepLoading
})

const doNotShowGasFundingStepAtom = atom((get) => {
  const route = get(routeAtom)

  return Boolean(route?.FeeRequirements?.is_funding_complete)
})

export const isFirstStepSuccessAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  const doNotShowGasFundingStep = get(doNotShowGasFundingStepAtom)

  if (doNotShowGasFundingStep) {
    return (
      routeMachineSnapshot.context.currentStep > 1 ||
      routeMachineSnapshot.value === 'Success'
    )
  }

  return (
    routeMachineSnapshot.context.currentStep > 1 ||
    routeMachineSnapshot.value === 'Success' ||
    routeMachineSnapshot.context.gasFunded
  )
})

export const getTradeStepRowAtom = (step: Step) =>
  atom((get) => {
    const routeMachineSnapshot = get(routeMachineSnapshotAtom)
    const context = routeMachineSnapshot.context
    const stepStates = context.routeStepStates
    const currentStepState = stepStates.find(
      (state) => state.stepNumber === step.step_number,
    )

    const signStartedAtMs =
      currentStepState?.approvalSignState?.startedAtMs ||
      currentStepState?.signState?.startedAtMs

    // Prefer model-derived createdAtMs over ephemeral state-machine timestamps when available
    // This can mean the user refreshed and we need to fall back to route model to get true start time
    // Fall back to current time if neither are available to ensure progress indicator renders
    const startedAtMs =
      currentStepState?.createdAtMs ?? signStartedAtMs ?? Date.now()

    const transferDetails = startedAtMs
      ? {
          startedAtMs,
          estimatedTimeMs: startedAtMs + step.duration_in_seconds * 1000,
        }
      : undefined

    const currentStepAborted =
      context.refundInfo?.stepNumber === step.step_number

    const isHotkeyActive =
      context.transferringFundsToSourceHotkeyState?.isSuccess

    // For MPC routes, user never needs to sign - autopilot handles it
    const isMpcRoute = context.shouldUseMpc

    return {
      isActive:
        (routeMachineSnapshot.value === 'ExecutingCurrentStep' &&
          step.step_number === context.currentStep) ||
        (routeMachineSnapshot.value === 'ExecutingNamadaUnshield' &&
          step.step_number === 1),
      isSigning: currentStepState?.signState?.isSigning,
      isLoading: currentStepState?.isLoading,
      isSuccess: currentStepState?.isSuccess,
      isError: currentStepState?.isError || currentStepAborted,
      error: currentStepState?.error,
      transferDetails,
      isUserSigning:
        (currentStepState?.signState?.isSigning &&
          !isHotkeyActive &&
          !isMpcRoute) ||
        false,
    }
  })

/** Route start time in UTC ms, preferring route model over live machine context. */
const routeStartedAtMsAtom = atom((get) => {
  const route = get(routeAtom)
  const snapshot = get(routeMachineSnapshotAtom)
  if (!route) return undefined

  // Prefer earliest valid step.created_at from the model
  let startMs = route.startTimeMs

  // If undefined, consider live session start or LastUpdated as fallback
  if (startMs === undefined) {
    if (
      snapshot?.context.route?.ID === route.ID &&
      snapshot.context.startedAtMs
    ) {
      startMs = snapshot.context.startedAtMs
    }
    if (startMs === undefined && route.LastUpdated) {
      const t = new Date(route.LastUpdated).getTime()
      if (Number.isFinite(t)) startMs = t
    }
  }

  return startMs
})

/** Returns route timing information for a given route or current route.
 *  startMs is the earliest valid step.created_at (sentinel guarded),
 *  endMs is startMs + DurationInSeconds*1000,
 *  routeId is the route identifier (useful to key animations).
 */
export const routeTimingAtom = atom((get) => {
  const route = get(routeAtom)
  const startMs = get(routeStartedAtMsAtom)
  if (!route) return undefined
  if (startMs === undefined) return undefined

  const endMs =
    startMs !== undefined ? startMs + route.DurationInSeconds * 1000 : undefined

  return { routeId: route.ID, startMs, endMs }
})

export const fromWalletAtom = atom((get) => {
  const { connectedWallets } = get(walletOptionsAtom)
  const walletKeyring = get(walletKeyringAtom)
  const sourceChain = get(routeAtom)?.sourceChain
  const route = get(routeAtom)

  if (!sourceChain || !route) {
    return undefined
  }

  const isValid = route.hasFinalSourceChainAddress(
    getEcosystemAddresses(walletKeyring),
  )

  return {
    ...connectedWallets[sourceChain.chain_type as ChainType],
    address: walletKeyring.getAddress(
      sourceChain.chain_type,
      sourceChain.chain_id,
    ),
    isValid,
    missingRequiredAddress: !isValid
      ? route.sourceChainEcosystemAddress
      : undefined,
  }
})

export const toWalletInfoAtom = atom((get) => {
  const recoveredWalletInfo = get(recoveredWalletInfoAtom)
  const route = get(routeAtom)

  // If we have recovered wallet info, use that
  if (recoveredWalletInfo) {
    return {
      address: recoveredWalletInfo.walletAddress,
      logoUrl: recoveredWalletInfo.walletLogo,
      displayName: recoveredWalletInfo.walletName,
      isAborting: recoveredWalletInfo.isAborting,
      isAbortSuccess: recoveredWalletInfo.isAbortSuccess,
    }
  }

  if (!route) {
    return undefined
  }

  // Search for account to get wallet provider imgs/display name
  const toAccount = get(destinationChainAccountAtom)
  const destChainAccountAddress = get(destinationChainAddressAtom)

  const logoUrl = toAccount?.provider_info.img
  let displayName = toAccount?.provider_info.name
  // If no account found and using destination address override, return special case
  if (!logoUrl && route.finalSwapAddressOverwrite) {
    displayName = 'Destination Address'
  }

  return {
    address: destChainAccountAddress,
    logoUrl,
    displayName,
  }
})

const recoveredWalletInfoAtom = atom((get) => {
  const route = get(routeAtom)
  const snapshot = get(routeMachineSnapshotAtom)
  const isAborting = get(isAbortingAtom)

  const isAbortSuccess =
    route?.isAborted || snapshot.value === 'FundsRecoverySuccess'

  if (!isAborting && !isAbortSuccess) {
    return null
  }

  const recoverAccount = get(recoverAccountAtom)
  const recoverAddress = get(recoverDestinationAddressAtom)

  if (recoverAccount) {
    return {
      walletName: recoverAccount.provider_info.name,
      walletAddress: recoverAddress,
      walletLogo: recoverAccount.provider_info.img,
      isAborting,
      isAbortSuccess,
    }
  }
})

export const isFirstStepErrorAtom = atom((get) => {
  const isFirstStep = get(isFirstStepAtom)
  const error = get(errorAtom)

  return isFirstStep && !!error
})

export const isSuccessAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)

  return snapshot.value === 'Success'
})

export const isAbortingAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)

  return (
    snapshot.hasTag('aborting') && snapshot.value !== 'FundsRecoverySuccess'
  )
})

export const isAbortedAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)

  return snapshot.value === 'FundsRecoverySuccess'
})

export const isCompleteAtom = atom((get) => {
  const isLastStep = get(isLastStepAtom)
  const isCurrentStepSuccess = get(isCurrentStepSuccessAtom)
  const isSuccess = get(isSuccessAtom)
  const isAborted = get(isAbortedAtom)

  return (isLastStep && isCurrentStepSuccess) || isSuccess || isAborted
})

export const isActiveAtom = atom((get) => {
  const isFirstStepLoading = get(isFirstStepLoadingAtom)
  const snapshot = get(routeMachineSnapshotAtom)

  return (
    isFirstStepLoading ||
    snapshot.hasTag('loading') ||
    snapshot.hasTag('aborting') ||
    snapshot.value === 'CurrentAutopilotStepFailed' ||
    snapshot.context.route?.isRunning ||
    (snapshot.context.currentStep === 1 &&
      snapshot.value !== 'Idle' &&
      snapshot.value !== 'WalletDisconnected')
  )
})

export const isRunningAtom = atom((get) => {
  const isActive = get(isActiveAtom)
  const error = get(errorAtom)

  return isActive && error === undefined
})

export const isTradeStartedAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)

  // For privacy vault swaps, consider trade as started when executing Namada unshield
  // since there's no user approval needed (MPC handles it automatically)
  if (snapshot.value === 'ExecutingNamadaUnshield') {
    return true
  }

  // For resumed routes, check if we're in a state that can only be reached after trade started.
  // This handles the case where context.isTradeStarted is false after page refresh but the
  // route is already in an active trade state.
  const activeTradeStates = [
    'StartMpcRoute',
    'DeterminingNextStep',
    'ExecutingCurrentStep',
    'TransferringFromAutopilotToDestination',
    'DelayedRetry',
  ]

  if (activeTradeStates.includes(snapshot.value as string)) {
    return true
  }

  return snapshot.context.isTradeStarted
})

export const cumulativeTransferDetailsAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  const route = routeMachineSnapshot?.context.route
  const isTradeStarted = get(isTradeStartedAtom)

  if (!routeMachineSnapshot || !route) {
    return
  }

  // Only show countdown once trade has actually started
  if (!isTradeStarted) {
    return
  }

  const startedAtMs = get(routeStartedAtMsAtom)
  if (startedAtMs !== undefined) {
    // Use route's original DurationInSeconds for smooth countdown
    // instead of recalculating based on actual step completion times
    return {
      startedAtMs,
      estimatedTimeMs: startedAtMs + route.DurationInSeconds * 1000,
    }
  }
})

export const estimatedRemainingTimeTextAtom = atom((get) => {
  const route = get(routeAtom)

  if (!route) {
    return
  }

  return humanizeSeconds(route.DurationInSeconds)
})

export const additionalFeesImageAtom = atom((get) => {
  const route = get(routeAtom)

  if (!route) {
    return
  }

  return route.FeeRequirements?.cost_check_result.fee.logo_img_url
})

export const additionalFeesAtom = atom((get) => {
  const route = get(routeAtom)

  if (!route) {
    return
  }

  return route.FeeRequirements?.cost_check_result.funds_required_display.usd
})

export const additionalFeesTextAtom = atom((get) => {
  const additionalFees = get(additionalFeesAtom)

  if (typeof additionalFees === 'undefined') {
    return
  }

  return formatPrice(additionalFees)
})

export const latestSignedStepAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.context.routeStepStates.findLast(
    (state) => state.signState?.txHash !== undefined,
  )
})

const currentStepStateAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.context.routeStepStates.find(
    (state) => state.stepNumber === routeMachineSnapshot.context.currentStep,
  )
})
//#endregion

//#region Current sign step

const isCurrentStepSuccessAtom = atom((get) => {
  const currentStepState = get(currentStepStateAtom)

  return currentStepState?.isSuccess
})

const isCurrentStepLoadingAtom = atom((get) => {
  const currentStepState = get(currentStepStateAtom)

  return currentStepState?.isLoading && !currentStepState.error
})

const currentStepErrorAtom = atom((get) => {
  const currentStepState = get(currentStepStateAtom)

  if (currentStepState?.error) {
    return currentStepState.error
  }

  // If this step was aborted due to backend-initiated refund aligning with this step,
  // surface the recovery message.
  if (currentStepState?.isAborted) {
    return new Error(
      'An issue was encountered with one of the providers in your trade route. Your funds have been recovered safely to your wallet.',
    )
  }

  // If current step is not running in actor (no snapshot), and the route has an error
  // at current step, return a generic error. We currently don't get error reasons from BE.
  if (currentStepState?.isError) {
    return new Error('Step failed')
  }
})

const isStepTransferringAtom = atom((get) => {
  const currentStepState = get(currentStepStateAtom)

  return (
    currentStepState?.signState?.isTracing ||
    currentStepState?.approvalSignState?.isTracing ||
    false
  )
})

export const isStepSigningAtom = atom((get) => {
  const currentStepState = get(currentStepStateAtom)

  return (
    currentStepState?.signState?.isSigning ||
    currentStepState?.approvalSignState?.isSigning ||
    false
  )
})

//#endregion

//#region Wallet connections

type InvalidWalletKeyring = {
  /** Not just wrong account, but disconnected wallet */
  isDisconnected: boolean
  chainName: string
  chainType: ChainType
  requiredAddress: string
}

export const invalidWalletKeyringAtom = atom<InvalidWalletKeyring | undefined>(
  (get) => {
    const routeMachineSnapshot = get(routeMachineSnapshotAtom)
    const route = routeMachineSnapshot.context.route
    const { data: mpcWalletAddresses } = get(
      mpcWalletAddressesWithNamadaOverrideAtom,
    )

    if (!route) return
    if (routeMachineSnapshot.value !== 'WalletDisconnected') return

    const walletEcoAddrs = {
      ...getEcosystemAddresses(routeMachineSnapshot.context.walletKeyring),
      namada: routeMachineSnapshot.context.enablePrivacy
        ? mpcWalletAddresses?.namada
        : undefined,
    }

    // Check source chain

    const srcChainKey = getChainTypeToEcosystemAddressesKey(
      route.sourceChain.chain_type,
    )
    if (walletEcoAddrs[srcChainKey] === undefined) {
      // Wallet is disconnected
      return {
        isDisconnected: true,
        chainName: route.sourceChain.chain_name,
        chainType: route.sourceChain.chain_type,
        requiredAddress:
          route.ecosystem_wallets!.ecosystemToAddrFinalDest[srcChainKey]!,
      }
    }

    if (!route.hasFinalSourceChainAddress(walletEcoAddrs)) {
      const requiredAddress = getRequiredAddressIfDifferent(
        route.sourceChain.chain_type,
        route.ecosystem_wallets!.ecosystemToAddrFinalDest,
        walletEcoAddrs,
      )
      if (requiredAddress) {
        return {
          isDisconnected: false,
          chainName: route.sourceChain.chain_name,
          chainType: route.sourceChain.chain_type,
          requiredAddress,
        }
      }
    }
  },
)

export const shouldUseGasStationAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context

  return !shouldSkipGasStation({ context })
})

export const isGasStepActiveAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  return routeMachineSnapshot.value === 'GasStation'
})

export const gasStepAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context
  const route = context.route
  const gasStationState = context.gasStationState

  // Check if there are actual fees to fund
  const totalFeesToFund =
    route?.FeeRequirements?.gas_station?.total_usd_to_fund ?? 0
  const hasFeesToFund = totalFeesToFund > 0

  // Gas station is needed if:
  // 1. There are actual fees to fund AND
  // 2. We have a gasStationState (went through it) OR shouldSkipGasStation is false (need to go through it)
  const isGasStationNeeded =
    hasFeesToFund &&
    (Boolean(gasStationState) || !shouldSkipGasStation({ context }))

  return {
    isActive: get(isGasStepActiveAtom),
    isGasStationNeeded,
    isSigning: gasStationState?.signState?.isSigning,
    isLoading: gasStationState?.isLoading,
    isSuccess:
      gasStationState?.isSuccess || route?.isHistoricalGasStationFundSuccess,
    isError: gasStationState?.isError,
    error: gasStationState?.error,
  }
})

//#region Transfer funds to hotkey

export const shouldTransferFundsToAutopilotWalletAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context

  return shouldTransferFundsToAutopilotWallet({ context })
})

export const shouldExecuteNamadaUnshieldAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context

  // Only for MPC-enabled Namada source chains
  if (!context.shouldUseMpc) return false
  if (context.route?.sourceChain.chain_type !== ChainType.Namada) return false

  return true
})

const isTransferringFundsToSourceHotkeyAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.value === 'TransferringFundsToSourceAutopilot'
})

const isExecutingNamadaUnshieldAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.value === 'ExecutingNamadaUnshield'
})

export const transferFundsToHotkeyStepAtom = atom((get) => {
  const transferFundsState = get(routeMachineSnapshotAtom).context
    .transferringFundsToSourceHotkeyState

  return {
    isActive: get(isTransferringFundsToSourceHotkeyAtom),
    isNeeded: get(shouldTransferFundsToAutopilotWalletAtom),
    isSigning: transferFundsState?.signState?.isSigning,
    isLoading: transferFundsState?.isLoading,
    isSuccess: transferFundsState?.isSuccess,
    isError: transferFundsState?.isError,
    error: transferFundsState?.error,
  }
})

export const namadaUnshieldStepAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context
  const step1State = context.routeStepStates.find(
    (state) => state.stepNumber === 1,
  )

  return {
    isActive: get(isExecutingNamadaUnshieldAtom),
    isNeeded: get(shouldExecuteNamadaUnshieldAtom),
    isSigning: step1State?.signState?.isSigning,
    isLoading: step1State?.isLoading,
    isSuccess: step1State?.isSuccess,
    isError: step1State?.isError,
    error: step1State?.error,
    isUserSigning: false, // Namada unshield is automated, never requires user signature
  }
})
//#endregion

//#region Trade

const isTradeStepActiveAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  return (
    routeMachineSnapshot.value === 'ExecutingCurrentStep' ||
    routeMachineSnapshot.value === 'ExecutingNamadaUnshield'
  )
})

export const tradeStepAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context
  const tradeStates = context.routeStepStates
  const currentTradeStepDetails = context.route?.Steps.find(
    (step) => step.step_number === context.currentStep,
  )
  const currentTradeState = tradeStates.find(
    (state) => state.stepNumber === context.currentStep,
  )

  const isHotkeyActive = context.transferringFundsToSourceHotkeyState?.isSuccess
  // Privacy vault swaps use MPC wallet which handles signing automatically
  const isPrivacyVaultSwap =
    context.shouldUseMpc &&
    context.route?.sourceChain.chain_type === ChainType.Namada

  return {
    isActive: get(isTradeStepActiveAtom),
    isSigning: currentTradeState?.signState?.isSigning,
    isLoading: currentTradeState?.isLoading,
    isSuccess: tradeStates.every((state) => state.isSuccess),
    isError: tradeStates.some((state) => state.isError),
    error: tradeStates.find((state) => state.isError)?.error,
    currentTradeStepDetails,
    transferDetails: get(cumulativeTransferDetailsAtom),
    isUserSigning:
      (currentTradeState?.signState?.isSigning &&
        !isHotkeyActive &&
        !isPrivacyVaultSwap) ||
      false,
  }
})

//#endregion

//#region Transfer funds from hotkey

const shouldTransferFundsBackFromAutopilotAtom = atom((get) => {
  const context = get(routeMachineSnapshotAtom).context

  return context.shouldUseMpc
})

export const isTransferringFromAutopilotToDestinationAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return routeMachineSnapshot.value === 'TransferringFromAutopilotToDestination'
})

export const transferFundsBackStepAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)
  const transferFundsState =
    routeMachineSnapshot.context.transferringFromHotkeyToDestinationState
  const context = routeMachineSnapshot.context

  const isHotkeyActive = context.transferringFundsToSourceHotkeyState?.isSuccess

  return {
    isActive: get(isTransferringFromAutopilotToDestinationAtom),
    isNeeded: get(shouldTransferFundsBackFromAutopilotAtom),
    isSigning: transferFundsState?.signState?.isSigning,
    isLoading: transferFundsState?.isLoading,
    isSuccess: transferFundsState?.isSuccess,
    isError: transferFundsState?.isError,
    error: transferFundsState?.error,
    isUserSigning:
      (transferFundsState?.signState?.isSigning && !isHotkeyActive) || false,
  }
})

//#endregion

//#region Hotkey transfers

const hotkeyTransferErrorAtom = atom((get) => {
  const routeMachineSnapshot = get(routeMachineSnapshotAtom)

  return (
    routeMachineSnapshot.context.transferringFromHotkeyToDestinationState
      ?.error ||
    routeMachineSnapshot.context.transferringFundsToSourceHotkeyState?.error
  )
})

//#region Effects

/**
 * We need to do that because we can easily get out of sync between react state from
 * wallet adapter and the routeMachine
 */
export const walletKeyringUpdateEffectAtom = atomEffect((get) => {
  const walletKeyring = get(walletKeyringAtom)
  const routeMachine = get(routeMachineAtom)

  if (walletKeyring) {
    routeMachine.send({ type: 'syncKeyrings', walletKeyring })
  }
})

export const routeUpdateEffectAtom = atomEffect((get) => {
  const routeMachine = get(routeMachineAtom)
  const route = get(routeAtom)
  const snapshot = routeMachine.getSnapshot()

  /**
   * We only reset if previously we had a valid route
   */
  if (
    route &&
    snapshot.context.route &&
    snapshot.context.route.ID !== route.ID
  ) {
    routeMachine.send({
      type: 'reset',
      input: getInitialInput({ get }),
    })
  }
})

export const amplitudeEventTracingAtom = atomEffect((get) => {
  const routeMachine = get(routeMachineAtom)
  const initialStartedAtMs = get(routeStartedAtMsAtom)

  const computeStartedAtMs = (): number | undefined => {
    const { context } = routeMachine.getSnapshot()
    let startMs = context.route?.startTimeMs

    if (startMs === undefined) {
      if (context.route && context.startedAtMs) {
        startMs = context.startedAtMs
      }
      if (startMs === undefined && context.route?.LastUpdated) {
        const t = new Date(context.route.LastUpdated).getTime()
        if (Number.isFinite(t)) startMs = t
      }
    }

    if (startMs === undefined) return initialStartedAtMs

    return startMs
  }

  const transferSubscription = routeMachine.on(
    'transferFromHotkeyToDestinationComplete',
    () => {
      if (ampli.isLoaded) {
        const route = routeMachine.getSnapshot().context.route!
        ampli.swapSwapSigned({
          ...makeSwapSignedProperties({ route }),
          page: getCurrentPageType(),
        })
      }
    },
  )

  const stepCompleteSubscription = routeMachine.on('stepComplete', () => {
    const context = routeMachine.getSnapshot().context

    if (context.currentStep - 1 === context.route?.firstStepNumber) {
      invalidateHistory()

      if (ampli.isLoaded) {
        const route = routeMachine.getSnapshot().context.route!
        ampli.swapSwapSigned({
          ...makeSwapSignedProperties({ route }),
          page: getCurrentPageType(),
        })
      }
    }
  })

  const routeRetry = routeMachine.on('retry', () => {
    ampli.swapSwapStepRetried()
  })

  const routeRetryableError = routeMachine.on('retryableError', () => {
    const context = routeMachine.getSnapshot().context
    const startedAtMs = computeStartedAtMs() ?? Date.now()

    const error = getDeepError(context.error)
    const errorMsg = error?.message ?? 'unknown'

    if (startedAtMs && ampli.isLoaded) {
      const route = routeMachine.getSnapshot().context.route!

      ampli.swapSwapStepFailed({
        ...makeSwapSignedComplete({ route }),
        page: getCurrentPageType(),
        timeToSwap: Date.now() - startedAtMs,
        errorMsg,
      })
    }

    safeTrackSwapError(error, {
      route: context.route,
      page: getCurrentPageType(),
      timeToSwap: startedAtMs ? Date.now() - startedAtMs : undefined,
      stepNumber: context.currentStep,
      errorType: 'retryable_error',
    })
  })

  const routeCompleteSubscription = routeMachine.on('routeComplete', () => {
    const startedAtMs = computeStartedAtMs()

    invalidateHistory()

    if (startedAtMs && ampli.isLoaded) {
      const route = routeMachine.getSnapshot().context.route!
      ampli.swapSwapCompleted({
        ...makeSwapSignedComplete({ route }),
        page: getCurrentPageType(),
        timeToSwap: Date.now() - startedAtMs,
      })
    }
  })

  const routeFailedSubscription = routeMachine.on('routeFailed', () => {
    const context = routeMachine.getSnapshot().context
    const startedAtMs = computeStartedAtMs() ?? Date.now()

    const error = getDeepError(context.error)
    const errorMsg = error?.message ?? 'unknown'

    if (startedAtMs && ampli.isLoaded) {
      const route = routeMachine.getSnapshot().context.route!
      ampli.swapSwapFailed({
        ...makeSwapSignedComplete({ route }),
        page: getCurrentPageType(),
        timeToSwap: Date.now() - startedAtMs,
        errorMsg,
      })
    }

    safeTrackSwapError(error, {
      route: context.route,
      page: getCurrentPageType(),
      timeToSwap: startedAtMs ? Date.now() - startedAtMs : undefined,
      stepNumber: context.currentStep,
      errorType: 'swap_failed',
    })
  })

  const abortRecoverySubscription = routeMachine.on(
    'recoveringAutopilotFundsComplete',
    () => {
      invalidateHistory()
    },
  )

  return () => {
    transferSubscription.unsubscribe()
    stepCompleteSubscription.unsubscribe()
    routeCompleteSubscription.unsubscribe()
    routeFailedSubscription.unsubscribe()
    routeRetryableError.unsubscribe()
    routeRetry.unsubscribe()
    abortRecoverySubscription.unsubscribe()
  }
})

/** Possible by user. */
export const isRetryPossibleAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)

  // Not possible for routes with privacy vault source chain
  if (
    snapshot.context.enablePrivacy &&
    snapshot.context.route?.sourceChain.chain_type === ChainType.Namada
  ) {
    return false
  }

  return (
    snapshot.hasTag('retryable') &&
    snapshot.context.maxRetries > 0 &&
    !snapshot.context.isMpcAutoRecoverActive
  )
})

export const routeEventTracingAtom = atomEffect((get, set) => {
  const routeMachine = get(routeMachineAtom)

  // Use the existing DataDog-integrated log function for setting up logger in route and gas station machines
  setGasStationMachineLogger(log)
  setRouteMachineLogger(log)

  // Subscribe to all state changes and log them
  const stateSubscription = routeMachine.subscribe((state) => {
    console.log('🔄 Route Machine State:', state.value)
    console.log('   Context:', {
      currentStep: state.context.currentStep,
      maxRetries: state.context.maxRetries,
      confirmed: state.context.confirmed,
      shouldUseMpc: state.context.shouldUseMpc,
      gasFunded: state.context.gasFunded,
      error: state.context.error?.message,
    })
    console.log('   Tags:', Array.from(state.tags))
  })

  const logSubscription = routeMachine.on('*', (event) => {
    const context = routeMachine.getSnapshot().context
    const logEvent = logEventFromRouteMachine(event, context)

    if (logEvent) {
      log(logEvent)
    }
  })

  const routeRevalidatedSubscription = routeMachine.on(
    'routeRevalidated',
    ({ route }) => {
      set(routeAtom, route)
    },
  )

  return () => {
    stateSubscription.unsubscribe()
    logSubscription.unsubscribe()
    routeRevalidatedSubscription.unsubscribe()
  }
})

export const pageTitleEffectAtom = atomEffect((get) => {
  const routeMachine = get(routeMachineAtom)
  const originalTitle = document.title

  /**
   * Computes the document title based on the current state of the route machine.
   */
  const getUpdatedTitle = (
    state: ReturnType<typeof routeMachine.getSnapshot>,
  ) => {
    const { route, currentStep, error } = state.context

    // If there is no route, revert to the original title.
    if (!route) {
      return originalTitle
    }

    // Determine whether the route still requires gas funding.
    const requiresGasFunding = !route.FeeRequirements?.is_funding_complete
    const requiresHotkeyTransfer =
      route.FeeRequirements?.is_multi_address_suggested
    let totalSteps = route.Steps.filter((step) => step.is_sign).length

    if (requiresGasFunding) {
      totalSteps += 1
    }

    if (requiresHotkeyTransfer) {
      totalSteps += 1
    }

    if (state.hasTag('loading')) {
      // If we are in the gas station state and gas funding is needed, always show step 1.
      if (state.matches('GasStation') && requiresGasFunding) {
        return `Step 1/${totalSteps} | ${originalTitle}`
      }
      // Otherwise, adjust the displayed step number if gas funding is part of the flow.
      const stepNumber = requiresGasFunding ? currentStep + 1 : currentStep
      return `Step ${stepNumber}/${totalSteps} | ${originalTitle}`
    }

    if (state.hasTag('final') && !error) {
      return `Transfer Complete | ${originalTitle}`
    }

    if (error) {
      return `Transfer Failed | ${originalTitle}`
    }

    return originalTitle
  }

  // Subscribe to the route machine and update the document title on each state change.
  const subscription = routeMachine.subscribe((state) => {
    document.title = getUpdatedTitle(state)
  })

  // Clean up: restore the original title and unsubscribe when this effect is disposed.
  return () => {
    document.title = originalTitle
    subscription.unsubscribe()
  }
})

//#endregion

//#region Source and Destination Chain Atoms

export const sourceChainAddressAtom = atom((get) => {
  const route = get(routeAtom)
  if (!route) return undefined

  return get(
    getConvertedChainAddressAtom(
      route.sourceChainEcosystemAddress,
      route.sourceChain.chain_id,
    ),
  )
})

export const sourceChainAccountAtom = atom((get) => {
  const route = get(routeAtom)
  if (!route) return undefined

  return get(
    getChainAccountAtom(
      route.sourceChainEcosystemAddress,
      route.sourceChain.chain_id,
    ),
  )
})

export const destinationChainAddressAtom = atom((get) => {
  const route = get(routeAtom)
  if (!route) return undefined

  return get(
    getConvertedChainAddressAtom(
      route.destinationChainEcosystemAddress,
      route.destinationChain.chain_id,
    ),
  )
})

export const destinationChainAccountAtom = atom((get) => {
  const route = get(routeAtom)
  if (!route) return undefined

  return get(
    getChainAccountAtom(
      route.destinationChainEcosystemAddress,
      route.destinationChain.chain_id,
    ),
  )
})

const recoverDestinationAddressAtom = atom((get) => {
  const snapshot = get(routeMachineSnapshotAtom)
  const route = get(routeAtom)
  if (!route) return undefined

  // Should already be in chain format coming from backend
  return snapshot.context.refundInfo?.destinationAddress
})

export const recoverAccountAtom = atom((get) => {
  const recoverAddress = get(recoverDestinationAddressAtom)
  const snapshot = get(routeMachineSnapshotAtom)

  if (!recoverAddress) return undefined

  const chainId = snapshot.context.refundInfo?.chainID

  if (!chainId) return undefined

  return get(getChainAccountAtom(recoverAddress, chainId))
})

//#endregion
